import { useState, useEffect, useCallback } from "react";
import { X, Info, AlertTriangle, AlertOctagon } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

interface Banner {
    id: string;
    message: string;
    priority: "info" | "warning" | "critical";
}

const PRIORITY_STYLES: Record<string, { bg: string; icon: typeof Info }> = {
    info: { bg: "bg-gradient-to-r from-violet-600 via-fuchsia-600 to-violet-600", icon: Info },
    warning: { bg: "bg-gradient-to-r from-amber-600 via-orange-500 to-amber-600", icon: AlertTriangle },
    critical: { bg: "bg-gradient-to-r from-red-700 via-red-600 to-red-700", icon: AlertOctagon },
};

export default function SystemBanner() {
    const [banners, setBanners] = useState<Banner[]>([]);
    const [dismissed, setDismissed] = useState<Set<string>>(new Set());

    const fetchBanners = useCallback(async () => {
        try {
            const res = await fetch(`${API}/api/banners/active`);
            if (res.ok) {
                const data = await res.json();
                setBanners(data);
            }
        } catch { /* silent */ }
    }, []);

    useEffect(() => {
        fetchBanners();
        const interval = setInterval(fetchBanners, 60_000);
        return () => clearInterval(interval);
    }, [fetchBanners]);

    const dismiss = (id: string) => {
        setDismissed((prev) => new Set(prev).add(id));
    };

    const visible = banners.filter((b) => !dismissed.has(b.id));
    if (visible.length === 0) return null;

    return (
        <div className="w-full z-[9999]" data-testid="system-banner-container">
            {visible.map((b) => {
                const style = PRIORITY_STYLES[b.priority] || PRIORITY_STYLES.info;
                const Icon = style.icon;
                return (
                <div
                    key={b.id}
                    className={`relative flex items-center justify-center gap-2 px-4 py-2 text-sm text-white ${style.bg}`}
                    data-testid={`system-banner-${b.id}`}
                >
                    <Icon className="w-4 h-4 shrink-0 opacity-80" />
                    <span className="text-center font-medium">{b.message}</span>
                    <button
                        onClick={() => dismiss(b.id)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-white/20 transition-colors"
                        aria-label="Dismiss banner"
                        data-testid={`dismiss-banner-${b.id}`}
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
                );
            })}
        </div>
    );
}
